import { memo, useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../store/hooks";

const SOURCE_LABEL: Record<string, string> = {
  internal: "Internal",
  allocation: "Allocation",
  redesign: "Redesign",
};

const InternalNotes = memo(() => {
  const notes = useAppSelector((state) => state.adminInternalNotes.notes);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  // Auto scroll when notes change (only if allowed)
  useEffect(() => {
    if (shouldAutoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [notes, shouldAutoScroll]);

  // Detect manual scroll
  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;

    setShouldAutoScroll(isAtBottom);
  };

  if (notes.length === 0) {
    return <p className="text-gray-500">No notes yet.</p>;
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="space-y-1 overflow-y-auto pr-1 flex-1"
    >
      {notes.map((note, idx) => {
        const source = note.source || "internal";
        const author = note.author || note.noted_by || "Unknown";
        const when = note.creation_time || note.created_at;
        const displayNote =
          (note.note === "Case allocated" ||
            (note.note === "No note provided" &&
              !String(note.QC2 || "").trim())) &&
          note.allocator
            ? `Allocated by ${note.allocator}`
            : note.note;
        return (
          <div key={idx} className="border p-1 rounded-md bg-gray-50">
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
              {SOURCE_LABEL[source] || source}
            </p>
            <p className="text-gray-800">{displayNote}</p>
            <p className="text-gray-500 text-[11px] mt-0">
              — {author},{" "}
              <span className="italic">
                {when ? new Date(when).toLocaleString() : "Unknown date"}
              </span>
            </p>
          </div>
        );
      })}
    </div>
  );
});

export default InternalNotes;
